import React, { useEffect, useState } from "react";
import { api, errMsg } from "../../lib/api";
import { Card, PageHeader, Pill, Btn } from "../../components/UI";
import { toast } from "sonner";
import { Undo2, X, Loader2, AlertCircle } from "lucide-react";

const usd = (n) => "$" + new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n || 0);
const STATUS_MAP = {
  paid: "Active", pending: "Pending approval", expired: "Rejected",
  failed: "Rejected", refunded: "Paused", partially_refunded: "Paused",
  disputed: "Pending approval", chargeback: "Rejected",
};

const REFUND_REASONS = [
  { key: "requested_by_customer", label: "Requested by donor" },
  { key: "duplicate", label: "Duplicate charge" },
  { key: "fraudulent", label: "Fraudulent / unauthorized" },
  { key: "other", label: "Other" },
];

export default function Transactions() {
  const [rows, setRows] = useState([]);
  const [refundTxn, setRefundTxn] = useState(null);

  const load = () => api.get("/donations/transactions").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="transactions-page">
      <PageHeader
        eyebrow="Fundraising · Ledger"
        title="Transactions"
        subtitle="Every Stripe checkout session — initiated, paid, expired, refunded, disputed — with campaign and microsite attribution."
      />
      <Card padded={false}>
        <table className="w-full text-sm" data-testid="transactions-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <th className="px-5 py-3">When</th>
              <th className="px-5 py-3">Donor</th>
              <th className="px-5 py-3">Amount</th>
              <th className="px-5 py-3">Fees</th>
              <th className="px-5 py-3">Net</th>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3">Source</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.session_id || t.id} className="border-b border-white/5" data-testid={`txn-row-${t.session_id || t.id}`}>
                <td className="px-5 py-2.5 text-xs text-slate-500 whitespace-nowrap">
                  {(t.created_at || "").slice(5, 16).replace("T", " ")}
                </td>
                <td className="px-5 py-2.5">
                  <div className="text-white text-xs">{t.donor?.name}</div>
                  <div className="text-[11px] text-slate-500">{t.donor?.email}</div>
                </td>
                <td className="px-5 py-2.5 font-mono text-white">{usd(t.amount)}</td>
                <td className="px-5 py-2.5 font-mono text-xs text-slate-400">{t.fees ? usd(t.fees.total_fees) : "—"}</td>
                <td className="px-5 py-2.5 font-mono text-xs text-emerald-300">{t.net_amount != null ? usd(t.net_amount) : "—"}</td>
                <td className="px-5 py-2.5 text-xs text-slate-400">{t.recurring ? "Monthly" : "One-time"}</td>
                <td className="px-5 py-2.5 text-xs text-slate-400">
                  {t.microsite_slug ? `/m/${t.microsite_slug}` : "direct"}
                  {t.utm_campaign && <div className="text-slate-600">utm: {t.utm_campaign}</div>}
                </td>
                <td className="px-5 py-2.5">
                  <Pill status={STATUS_MAP[t.payment_status] || "Pending approval"}>{t.payment_status}</Pill>
                  {t.refund?.amount && (
                    <div className="text-[10px] text-slate-500 mt-0.5">Refund: {usd(t.refund.amount)}</div>
                  )}
                  {t.dispute && (
                    <div className="text-[10px] text-amber-300 mt-0.5">Dispute: {t.dispute.reason}</div>
                  )}
                </td>
                <td className="px-5 py-2.5 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    {t.payment_status === "paid" && (
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/political-os/donor-receipts/resend/${t.session_id || t.id}`);
                            toast.success("Receipt re-sent to " + (t.donor?.email || "donor"));
                          } catch (e) { toast.error(errMsg(e, "Could not resend receipt")); }
                        }}
                        data-testid={`resend-receipt-${t.session_id || t.id}`}
                        className="text-xs px-2 py-1 rounded-md border border-white/15 text-slate-400 hover:border-cyan-400/40 hover:text-cyan-300"
                        title={t.receipt_sent_to ? `Last sent to ${t.receipt_sent_to}` : "Send donor receipt"}
                      >
                        Receipt
                      </button>
                    )}
                    {t.payment_status === "paid" && t.stripe_payment_intent_id && (
                      <button
                        onClick={() => setRefundTxn(t)}
                        data-testid={`refund-btn-${t.session_id || t.id}`}
                        className="text-xs px-2.5 py-1 rounded-md border border-white/15 text-slate-300 hover:border-rose-400/50 hover:text-rose-300 inline-flex items-center gap-1"
                      >
                        <Undo2 className="w-3 h-3" /> Refund
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan="9" className="px-5 py-8 text-center text-sm text-slate-500">No transactions yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {refundTxn && (
        <RefundModal
          txn={refundTxn}
          onClose={() => setRefundTxn(null)}
          onDone={() => { setRefundTxn(null); load(); }}
        />
      )}
    </div>
  );
}

function RefundModal({ txn, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("requested_by_customer");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (isPartial) => {
    setBusy(true);
    try {
      const payload = { reason, note };
      if (isPartial) {
        const a = Number(amount);
        if (!(a > 0) || a > Number(txn.amount)) {
          setBusy(false);
          return toast.error("Enter a partial amount between 0 and the original charge");
        }
        payload.amount = a;
      }
      const { data } = await api.post(`/donations/transactions/${txn.session_id || txn.id}/refund`, payload);
      toast.success(`Refunded ${usd(data.refunded_amount)} via Stripe`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e, "Refund failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/70 flex items-center justify-center p-4" data-testid="refund-modal">
      <Card className="max-w-md w-full !p-0">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-cyan-300">Refund transaction</div>
            <div className="font-display text-lg font-bold text-white mt-0.5">{usd(txn.amount)} · {txn.donor?.name || txn.donor?.email}</div>
          </div>
          <button onClick={onClose} data-testid="refund-close" className="text-slate-500 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-slate-400 bg-white/[0.03] border border-white/10 rounded-md p-3 flex gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-300" />
            <div>
              Refunds hit Stripe immediately. Donation removed from the total raised and marked as refunded/partially refunded. The donor will see the credit in 5-10 business days.
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Reason</label>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
                    data-testid="refund-reason"
                    className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none">
              {REFUND_REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Note (optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)}
                      data-testid="refund-note" rows={2}
                      placeholder="Why are we refunding? Anything the audit trail should record."
                      className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none placeholder-slate-500" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Partial amount (USD)</label>
            <input type="number" min="0" step="0.01" max={txn.amount}
                   value={amount} onChange={(e) => setAmount(e.target.value)}
                   data-testid="refund-amount"
                   placeholder={`Leave blank for a full refund of ${usd(txn.amount)}`}
                   className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none" />
          </div>
        </div>
        <div className="px-5 pb-5 flex gap-2 justify-end">
          <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
          {amount && Number(amount) > 0 && Number(amount) < Number(txn.amount) ? (
            <Btn onClick={() => submit(true)} data-testid="refund-submit-partial" disabled={busy}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
              Refund {usd(amount)}
            </Btn>
          ) : (
            <Btn onClick={() => submit(false)} data-testid="refund-submit-full" disabled={busy}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
              Refund full {usd(txn.amount)}
            </Btn>
          )}
        </div>
      </Card>
    </div>
  );
}
